import { inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { extractApiError, extractHttpError } from '@shared/utils/error.utils';

/**
 * Valores admitidos en un query param.
 *
 * El arreglo no es un adorno: hay endpoints con query REPETIBLE
 * (`?anios=2025&anios=2026`). `HttpClient` repite la clave por elemento cuando
 * recibe un arreglo, que es justo lo que esperan. Con la firma anterior
 * —`Record<string, string>`— esa llamada no se podia ni escribir, y el generador
 * terminaba emitiendo el parametro como escalar.
 */
/**
 * Un error de API que CONSERVA el codigo HTTP.
 *
 * ── POR QUE NO BASTA UN `Error` NORMAL ────────────────────────────────────
 *
 * Porque al convertir el `HttpErrorResponse` en un `Error` con el mensaje ya
 * extraido se pierde el estado, y quien lo recibe no puede distinguir «esto no
 * existe» de «el servidor se cayo». La unica salida que queda es tragarse todos
 * los errores por igual, o ninguno.
 *
 * El caso que lo destapo, en `frecursoshumanos`: `/api/Empleado/me` responde
 * 404 cuando la cuenta no tiene expediente —el superadministrador, por
 * ejemplo— y eso NO es un fallo: es la respuesta. Pero llegaba como error rojo
 * en consola, identico a un 500 de verdad.
 *
 * ⚠️ Vive AQUI y no en `error.utils` a proposito: esta clase la emite el
 * generador junto con la base que la lanza, asi que las dos se mueven juntas.
 * Si viviera en `error.utils` —que se escribe a mano— habria un ciclo de
 * importacion, porque esta base ya importa de ahi.
 */
export class ErrorDeApi extends Error {
  constructor(
    mensaje: string,
    readonly estado: number,
  ) {
    super(mensaje);
    this.name = 'ErrorDeApi';
  }
}

/**
 * Si el error es un 404 de la API.
 *
 * Va junto a la clase porque es su unico uso real: sin el, cada llamador
 * repetiria el `instanceof` y el numero.
 *
 *     catchError((e) => (esNoEncontrado(e) ? of(null) : throwError(() => e)))
 */
export function esNoEncontrado(error: unknown): boolean {
  return error instanceof ErrorDeApi && error.estado === 404;
}

export type QueryParams = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean>
>;

/**
 * Constraint para cualquier ResponseDto del API.
 * Cubre ambas variantes: con y sin `errors`.
 */
interface ApiResponseBase {
  errored?: boolean;
  errorMessage?: string | null;
  errors?: string[] | null;
  object?: unknown;
}

/**
 * Extrae el tipo de `object` de un ResponseDto.
 *
 * Ejemplo:
 *   ExtractObject<CatMainStagesIEnumerableResponseDto>
 *   → CatMainStages[] | null | undefined
 *
 *   NonNullable<ExtractObject<...>>
 *   → CatMainStages[]
 */
type ExtractObject<T extends ApiResponseBase> = T['object'];

/**
 * Servicio base para llamadas HTTP tipadas con manejo de errores.
 *
 * Usa los tipos DTO reales de los schemas como genérico. El tipo de
 * retorno se infiere automáticamente desde la propiedad `object` del DTO:
 *
 * ```typescript
 * export class ProspectService extends ApiBaseService {
 *   async getStages() {
 *     return this.get<CatMainStagesIEnumerableResponseDto>(url);
 *     // Retorna: CatMainStages[] | null | undefined
 *   }
 * }
 * ```
 *
 * Flujo:
 * 1. Convierte Observable → Promise (async/await)
 * 2. Verifica `errored === true` → lanza Error con mensaje del backend
 * 3. Retorna `response.object` con el tipo real del DTO
 * 4. Si la petición HTTP falla, extrae mensaje del body de error
 */
export abstract class ApiBaseService {
  protected http = inject(HttpClient);

  protected async get<TResponse extends ApiResponseBase>(
    url: string,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('GET', url, undefined, params);
  }

  protected async post<TResponse extends ApiResponseBase>(
    url: string,
    body: unknown,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('POST', url, body, params);
  }

  protected async put<TResponse extends ApiResponseBase>(
    url: string,
    body: unknown,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('PUT', url, body, params);
  }

  protected async patch<TResponse extends ApiResponseBase>(
    url: string,
    body: unknown,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('PATCH', url, body, params);
  }

  protected async delete<TResponse extends ApiResponseBase>(
    url: string,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    return this.request<TResponse>('DELETE', url, undefined, params);
  }

  /**
   * Maneja respuestas que pueden venir envueltas en ResponseDto o no.
   * Útil cuando el schema no declara el wrapper pero el backend lo usa.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async safeRequest<T>(obs: import('rxjs').Observable<any>): Promise<T> {
    try {
      const response: any = await lastValueFrom(obs);
      if (response && typeof response === 'object' && 'object' in response) {
        this.assertNoError(response);
        return response.object as T;
      }
      return response as T;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        // `ErrorDeApi` y no `Error`: conserva el codigo HTTP, que es lo unico que
        // permite a quien llama distinguir «no existe» de «se cayo». El mensaje
        // sigue siendo exactamente el mismo.
        throw new ErrorDeApi(extractHttpError(error), error.status);
      }
      throw error;
    }
  }

  private async request<TResponse extends ApiResponseBase>(
    method: string,
    url: string,
    body?: unknown,
    params?: QueryParams,
  ): Promise<ExtractObject<TResponse>> {
    try {
      const response = await lastValueFrom(
        this.http.request<TResponse>(method, url, { body, params }),
      );
      this.assertNoError(response);
      return response.object as ExtractObject<TResponse>;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        // `ErrorDeApi` y no `Error`: conserva el codigo HTTP, que es lo unico que
        // permite a quien llama distinguir «no existe» de «se cayo». El mensaje
        // sigue siendo exactamente el mismo.
        throw new ErrorDeApi(extractHttpError(error), error.status);
      }
      throw error;
    }
  }

  private assertNoError(response: ApiResponseBase): void {
    const error = extractApiError(response);
    if (error) throw new Error(error);
  }
}
